// Trusted hosts for BE-provided redirect destinations. Same-origin is always allowed; any
// other host must be an exact match of, or a subdomain of, these Crehana domains.
//
// Twin of `apps/crehana-frontend/src/views/Auth/pages/B2BOrgResetPassword/utils/is-trusted-redirect-url.ts`.
// It is duplicated rather than imported because these are two separate Next.js apps with no
// shared module boundary between them; keep both copies in sync if the host list changes.
const TRUSTED_HOST_SUFFIXES = ['crehana.com', 'creha.co'] as const;

/**
 * Open-redirect guard for post-login navigation. The `redirect` / `next_url` the login
 * mutations echo back originates in the `?next=` query param, so it is attacker-
 * influenceable end to end. Before navigating the browser we confirm it points either at
 * our own origin or at a trusted Crehana domain. Anything else (or an unparseable url) is
 * rejected so the caller can fall back to a safe relative path.
 *
 * @param value    The candidate url (e.g. the mutation's `redirect`).
 * @param origin   The current window origin. Passed in (not read globally) so the util is
 *                 pure and unit-testable.
 */
export const isTrustedRedirectUrl = (
  value: string,
  origin: string,
): boolean => {
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return false;
  }

  // Only http(s) may drive a browser navigation (blocks javascript:, data:, etc.).
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return false;
  }

  if (url.origin === origin) {
    return true;
  }

  const host = url.hostname.toLowerCase();
  return TRUSTED_HOST_SUFFIXES.some(
    suffix => host === suffix || host.endsWith(`.${suffix}`),
  );
};
